import Vue from "vue";
import { reflectKeys, jwt, appLocalStorage } from '@/app/shared/services';

import { fetchGuide, fetchTags, fetchArtefacts, fetchArtefact, fetchArtefactUnit } from '../services';

/** Initial state */
const initialState = {
  apiCallStatusTrack: {
    guide: false,
    tags: false,
    artefacts: false,
    artefact: false,
    artefactUnit: false,
    payload: false
  },
  data: null,
  error: null,
  guide: [],
  tags: [],
  artefacts: [],
  artefact: {},
  artefactUnit: {}
};

/** Prefix for mutation types and actiontypes */
const namespacedPrefix = '[ANALYZE]';

/**
 * Mutation types
 */
const mutationTypes = reflectKeys(
  [
    'ANALYZE_DATA_REQUEST',
    'ANALYZE_DATA_SUCCESS',
    'ANALYZE_DATA_ERROR',
    'ANALYZE_DATA_RESET',
    'LOOKUP_ANALYZE_GUIDE',
    'SET_ANALYZE_GUIDE',
    'SET_ANALYZE_GUIDE_ON',
    'LOOKUP_ANALYZE_TAGS',
    'SET_ANALYZE_TAGS',
    'LOOKUP_ANALYZE_ARTEFACTS',
    'SET_ANALYZE_ARTEFACTS',
    'LOOKUP_ANALYZE_ARTEFACT',
    'SET_ANALYZE_ARTEFACT',
    'LOOKUP_ANALYZE_ARTEFACT_UNIT',
    'SET_ANALYZE_ARTEFACT_UNIT',
    'LOOKUP_ANALYZE_PAYLOAD',
    'SET_ANALYZE_PAYLOAD',
    'RESET_ANALYZE'
  ],
  namespacedPrefix
);

const {
  ANALYZE_DATA_REQUEST,
  ANALYZE_DATA_SUCCESS,
  ANALYZE_DATA_ERROR,
  ANALYZE_DATA_RESET,
  LOOKUP_ANALYZE_GUIDE,
  SET_ANALYZE_GUIDE,
  SET_ANALYZE_GUIDE_ON,
  LOOKUP_ANALYZE_TAGS,
  SET_ANALYZE_TAGS,
  LOOKUP_ANALYZE_ARTEFACTS,
  SET_ANALYZE_ARTEFACTS,
  LOOKUP_ANALYZE_ARTEFACT,
  SET_ANALYZE_ARTEFACT,
  LOOKUP_ANALYZE_ARTEFACT_UNIT,
  SET_ANALYZE_ARTEFACT_UNIT,
  LOOKUP_ANALYZE_PAYLOAD,
  SET_ANALYZE_PAYLOAD,
  RESET_ANALYZE
} = mutationTypes;

/**
 * Auth data mutations
 */
const mutations = {
  /** auth data request */
  [ANALYZE_DATA_REQUEST](state) {
    Object.assign(state, { error: null });
  },

  /** auth data success */
  [ANALYZE_DATA_SUCCESS](state, payload) {
    Object.assign(state, { apiCallStatusTrack: initialState.apiCallStatusTrack, data: payload, user: payload, isAuthenticated: true });
  },

  /** auth data error */
  [ANALYZE_DATA_ERROR](state, payload) {
    Object.assign(state, {
      apiCallStatusTrack: initialState.apiCallStatusTrack,
      data: null,
      error: payload || true
    });
  },

  /** reset auth data */
  [ANALYZE_DATA_RESET](state) {
    Object.assign(state, initialState);
  },

  [LOOKUP_ANALYZE_GUIDE](state) {
    state.apiCallStatusTrack.guide = true;
  },
  [SET_ANALYZE_GUIDE](state, payload) {
    state.guide = payload.res.data;
    state.apiCallStatusTrack.guide = false;
  },
  [SET_ANALYZE_GUIDE_ON](state, guide) {
    state.guideOn = guide;
  },
  [LOOKUP_ANALYZE_TAGS](state) {
    state.apiCallStatusTrack.tags = true;
  },
  [SET_ANALYZE_TAGS](state, payload) {
    state.tags = payload.res.data;
    state.tagsCount = payload.res.data ? payload.res.data.length : 0;
    state.apiCallStatusTrack.tags = false;
  },
  [LOOKUP_ANALYZE_ARTEFACTS](state) {
    state.apiCallStatusTrack.artefacts = true;
  },
  [SET_ANALYZE_ARTEFACTS](state, payload) {
    state.artefacts = payload.artefacts;
    state.artefactsCount = payload.artefacts ? payload.artefacts.length : 0;
    state.apiCallStatusTrack.artefacts = false;
  },
  [LOOKUP_ANALYZE_ARTEFACT](state) {
    state.apiCallStatusTrack.artefact = true;
  },
  [SET_ANALYZE_ARTEFACT](state, payload) {
    state.artefact = {};
    state.artefact = payload;
    state.apiCallStatusTrack.artefact = false;
  },
  [LOOKUP_ANALYZE_ARTEFACT_UNIT](state) {
    state.apiCallStatusTrack.artefactUnit = true;
  },
  [SET_ANALYZE_ARTEFACT_UNIT](state, payload) {
    state.artefactUnit = payload;
    state.apiCallStatusTrack.artefactUnit = false;
  },
  [LOOKUP_ANALYZE_PAYLOAD](state) {
    state.apiCallStatusTrack.payload = true;
  },
  [SET_ANALYZE_PAYLOAD](state, payload) {
    state.artefact = payload;
    state.apiCallStatusTrack.payload = false;
  },
  [RESET_ANALYZE](state) {
    for (let f in state) {
      Vue.set(state, f, state.initialState[f]);
    }
  }

};

/** Actions types constants */
export const actionsTypes = reflectKeys([
  'FETCH_ANALYZE_GUIDE',
  'FETCH_ANALYZE_TAGS',
  'FETCH_ANALYZE_ARTEFACTS',
  'FETCH_ANALYZE_ARTEFACT',
  'FETCH_ANALYZE_ARTEFACT_UNIT',
  'FETCH_ANALYZE_PAYLOAD',
  'RESET_ANALYZE_STATE'
], namespacedPrefix);

/**
 * Users data actions
 */
const actions = {

  async [actionsTypes.FETCH_ANALYZE_GUIDE](context, payload) {
    context.commit(LOOKUP_ANALYZE_GUIDE);
    return await fetchGuide(payload)
      .then(data => {
        payload.res = data;
        context.commit(SET_ANALYZE_GUIDE, payload);
      })
      .catch(error => {
        payload.res = error;
        context.commit(SET_ANALYZE_GUIDE, payload);
      });
  },
  async [actionsTypes.FETCH_ANALYZE_TAGS]({ commit }, payload) {
    commit(LOOKUP_ANALYZE_TAGS);
    return await fetchTags(payload)
      .then(data => {
        payload.res = data;
        commit(SET_ANALYZE_TAGS, payload);
      })
      .catch(error => {
        payload.res = error;
        commit(SET_ANALYZE_TAGS, payload);
      });
  },
  async [actionsTypes.FETCH_ANALYZE_ARTEFACTS]({ commit }, payload) {
    commit(LOOKUP_ANALYZE_ARTEFACTS);
    return await fetchArtefacts(payload)
      .then(data => {
        payload.res = data;
        commit(SET_ANALYZE_ARTEFACTS, payload);
      })
      .catch(error => {
        payload.res = error;
        commit(SET_ANALYZE_ARTEFACTS, payload);
      });
  },
  async [actionsTypes.FETCH_ANALYZE_ARTEFACT](context, payload, prevArtifact) {
    // avoid extronuous network call if search exists
    context.commit(LOOKUP_ANALYZE_ARTEFACT);
    if (prevArtifact !== undefined) {
      return context.commit(SET_ANALYZE_ARTEFACT, prevArtifact);
    }
    return await fetchArtefact(payload)
      .then(data => {
        payload.res = data.data;
        context.commit(SET_ANALYZE_ARTEFACT, payload);
      })
      .catch(error => {
        payload.res = { error: 'No records found for the search criteria.' };
        context.commit(SET_ANALYZE_ARTEFACT, payload);
        // throw new Error(error);
      });
  },
  async [actionsTypes.FETCH_ANALYZE_ARTEFACT_UNIT](context, payload, prevArtifact) {
    // avoid extronuous network call if search exists
    context.commit(LOOKUP_ANALYZE_ARTEFACT_UNIT);
    if (prevArtifact !== undefined) {
      return context.commit(SET_ANALYZE_ARTEFACT_UNIT, prevArtifact);
    }
    return await fetchArtefactUnit(payload)
      .then(data => {
        payload.res.order = data.data;
        context.commit(SET_ANALYZE_ARTEFACT_UNIT, payload);
      })
      .catch(error => {
        payload.res = { error: 'No records found for the given criteria.' };
        context.commit(SET_ANALYZE_ARTEFACT_UNIT, payload);
      });
  },
  async [actionsTypes.FETCH_ANALYZE_PAYLOAD](context, payload, prevArtifact) {
    // avoid extronuous network call if search exists
    context.commit(LOOKUP_ANALYZE_PAYLOAD);
    if (prevArtifact !== undefined) {
      return context.commit(SET_ANALYZE_PAYLOAD, prevArtifact);
    }

    context.commit(SET_ANALYZE_PAYLOAD, payload);
    return await payload;
  },

  [actionsTypes.RESET_ANALYZE_STATE]({ commit }) {
    commit(RESET_ANALYZE);
  }
};

/**
 * Users data actions
 */
const state = {
  ...initialState,
  guideOn: [],
  loading: true,
  tagsCount: 0,
  artefactsCount: 0
};

const getters = {
  isLoading(state) {
    state.loading = false;
    for (let x in state.apiCallStatusTrack) {
      if(state.apiCallStatusTrack[x]) {
        state.loading = true;
        break;
      }
    }
    return state.loading;
  },
  tagsCount(state) {
    return state.tagsCount;
  },
  tags(state) {
    return state.tags;
  },
  artefactsCount(state) {
    return state.artefactsCount;
  },
  artefacts(state) {
    return state.artefacts;
  },
  artefact(state) {
    return state.artefact;
  },
  artefactUnit(state) {
    return state.artefactUnit;
  },
  guide(state) {
    return state.guide;
  },
  guideOn(state) {
    return state.guideOn;
  }
};

export default {
  mutations,
  actions,
  state,
  getters
};
